import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Alert, Box, Button, Paper, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { SlRefresh } from 'react-icons/sl'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import { formatIsoDateTime } from '../../helper/odulOneriTracking'

const formatRemaining = (dueAt) => {
  if (!dueAt) return '—'
  const due = new Date(dueAt).getTime()
  if (Number.isNaN(due)) return '—'
  const diffMs = due - Date.now()
  const absHours = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60))
  const days = Math.floor(absHours / 24)
  const hours = absHours % 24
  if (diffMs < 0) {
    if (days > 0) return `${days} gün ${hours} saat gecikmiş`
    return `${hours} saat gecikmiş`
  }
  if (days > 0) return `${days} gün ${hours} saat`
  return `${hours} saat`
}

/** Yönetici paneli — bekleyen kayıtlar (değerlendirme e-postasındaki bağlantı kullanılır) */
const YoneticiDashboard = () => {
  const { currentUser } = useSelector((state) => state.auth)
  const { fetchManagerPendingTasks, isDepartmentManagerEmail } = useOdulOneriCall()

  const [loading, setLoading] = useState(true)
  const [authorized, setAuthorized] = useState(false)
  const [rows, setRows] = useState([])

  const loadData = useCallback(async () => {
    if (!currentUser) return
    setLoading(true)
    try {
      const [tasks, isManager] = await Promise.all([
        fetchManagerPendingTasks(currentUser),
        isDepartmentManagerEmail(currentUser),
      ])
      setRows(tasks)
      setAuthorized(Boolean(isManager || tasks.length > 0))
    } finally {
      setLoading(false)
    }
  }, [currentUser, fetchManagerPendingTasks, isDepartmentManagerEmail])

  useEffect(() => {
    loadData()
  }, [loadData])

  const tableRows = useMemo(
    () =>
      rows.map((record) => ({
        id: record.id,
        recordNumber: record.recordNumber || '—',
        applicantName: record.applicant?.fullName || '—',
        department: record.relatedDepartmentName || '—',
        facility: record.applicant?.location || '—',
        topic: record.topic || '—',
        createdAtLabel: formatIsoDateTime(record.createdAt),
        remaining: formatRemaining(record.dueAt),
      })),
    [rows]
  )

  const columns = useMemo(
    () => [
      { field: 'recordNumber', headerName: 'Kayıt No', minWidth: 140, flex: 1 },
      { field: 'applicantName', headerName: 'Başvuru Yapan', minWidth: 160, flex: 1 },
      { field: 'department', headerName: 'Bölüm', minWidth: 140, flex: 1 },
      { field: 'facility', headerName: 'Tesis', minWidth: 120, flex: 0.8 },
      { field: 'topic', headerName: 'Konu', minWidth: 200, flex: 1.5 },
      { field: 'createdAtLabel', headerName: 'Başvuru Tarihi', minWidth: 150, flex: 1 },
      { field: 'remaining', headerName: 'Kalan SLA', minWidth: 140, flex: 1 },
    ],
    []
  )

  if (!loading && !authorized) {
    return (
      <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 1.5, sm: 2 }, py: 2, mt: 10, boxSizing: 'border-box' }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography fontWeight={700} gutterBottom>
            Erişim Yok
          </Typography>
          <Typography variant="body2">
            Bu panel yalnızca bölüm yöneticileri içindir. Hesabınız (
            {currentUser || '—'}) yönetici olarak tanımlı değil.
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 1.5, sm: 2 }, py: 2, mt: 10, boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography fontWeight={700} letterSpacing={1}>
          Ödül Öneri — Yönetici Paneli
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            component={RouterLink}
            to="/odul-oneri/opex-takip"
            sx={{ textTransform: 'none' }}
          >
            Uygulama Takibi
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SlRefresh size={16} />}
            onClick={loadData}
            sx={{ textTransform: 'none' }}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Değerlendirme, size gönderilen e-postadaki güvenli bağlantı üzerinden yapılır. Bu tablo
        yalnızca bekleyen kayıt numaralarını gösterir. Kabul sonrası uygulama durumunu{' '}
        <strong>Uygulama Takibi</strong> ekranından güncelleyebilirsiniz.
      </Alert>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Onay bekleyen görevler ({currentUser}) — {tableRows.length} kayıt
      </Typography>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <DataGrid
          loading={loading}
          rows={tableRows}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            minWidth: 720,
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: 'action.hover' },
          }}
        />
      </Box>
    </Box>
  )
}

export default YoneticiDashboard
